import React, { useRef, useState } from 'react';
import './DropZone.css';

interface DropZoneProps {
  onFileDrop: (files: FileList) => void;
  accept?: string;
  enabled?: boolean;
  label?: string;
  className?: string;
  children: React.ReactNode;
}

export default function DropZone({
  onFileDrop,
  accept,
  enabled = true,
  label = 'Drop file here',
  className,
  children,
}: DropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!enabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDragOver(true);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!enabled) return;
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (!enabled) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (accept) {
      const patterns = accept.split(',').map(p => p.trim());
      const file = files[0];
      const matches = patterns.some(pattern => {
        if (pattern.endsWith('/*')) {
          return file.type.startsWith(pattern.replace('/*', '/'));
        }
        return file.type === pattern;
      });
      if (!matches) return;
    }

    onFileDrop(files);
  }

  return (
    <div
      className={`dropzone-wrapper${className ? ' ' + className : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragOver && (
        <div className="dropzone-overlay">
          <div className="dropzone-overlay-content">
            <span className="dropzone-plus">+</span>
            <span className="dropzone-label">{label}</span>
          </div>
        </div>
      )}
    </div>
  );
}
